import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, isDevMode } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ICustomProperties } from '@microsoft/applicationinsights-core-js';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { buildVariables } from '@th-common/assets/config/build-variables';
import { filter } from 'rxjs/operators';

import { APP_CONFIG } from '@core/constants/app-config.constants';
import { IAppConfig } from '@core/interfaces/app-config.interface';

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights;

  constructor(private readonly router: Router, @Inject(APP_CONFIG) private appConfig: IAppConfig) {
    if (!isDevMode()) {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: appConfig.appInsightsKey,
        },
      });
      this.appInsights.loadAppInsights();
      this.loadCustomTelemetryProperties();
      this.createRouterSubscription();
    }
  }

  setAIUserId(userId: string, accountId: string = '', storeInCookie: boolean = true) {
    if (!isDevMode() && !!userId) {
      this.appInsights.setAuthenticatedUserContext(userId, accountId, storeInCookie);
    }
  }

  clearAIUserId() {
    if (!isDevMode()) {
      this.appInsights.clearAuthenticatedUserContext();
    }
  }

  logPageView(name?: string, uri?: string) {
    if (!isDevMode()) {
      this.appInsights.trackPageView({
        name,
        uri,
      });
    }
  }

  logException(exception: Error, customProperties: ICustomProperties = {}, severityLevel?: SeverityLevel) {
    if (!isDevMode()) {
      if (!!exception && !(exception instanceof HttpErrorResponse)) {
        if (exception.stack) {
          exception.name = exception.name || exception.stack.split('\n')[0];
          exception.message = exception.message || exception.stack.split('\n')[0];
        }
        this.appInsights.trackException({ exception }, customProperties);
      }
    }
  }

  logEvent(name: string, properties?: {[key: string]: any}) {
    if (!isDevMode()) {
      this.appInsights.trackEvent({ name }, properties);
    }
  }

  logMetric(name: string, average: number, properties?: {[key: string]: any}) {
    if (!isDevMode()) {
      this.appInsights.trackMetric({ name, average }, properties);
    }
  }

  logTrace(message: string, properties?: {[key: string]: any}) {
    if (!isDevMode()) {
      this.appInsights.trackTrace({ message }, properties);
    }
  }

  private loadCustomTelemetryProperties() {
    this.appInsights.addTelemetryInitializer(envelope => {
      const item = envelope.baseData;
      item.properties = item.properties || {};
      item.properties.PortalType = this.appConfig.portalType;
      item.properties.FEBuildNumber = buildVariables.buildNumber;
    });
  }

  private createRouterSubscription() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.logPageView(null, event.urlAfterRedirects);
    });
  }
}
